// Generic, throttled MFE prefetcher: one at a time, idle-only, network-aware
import { AuthStateManager } from '@innolab-trader/mfe-shared'

export type PrefetchTask = () => Promise<void>

const prefetchQueue: PrefetchTask[] = []
let prefetchRunning = false

function shouldPrefetch(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nav: any = navigator as any
    const conn = nav?.connection
    if (conn?.saveData) return false
    const et = conn?.effectiveType as string | undefined
    if (et && (et.includes('2g') || et === 'slow-2g')) return false
  } catch {
    // Network API not available - assume prefetch is safe
  }
  return true
}

function runWhenIdle(fn: () => void) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (window as any).requestIdleCallback === 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(window as any).requestIdleCallback(fn, { timeout: 1500 })
  } else {
    setTimeout(fn, 0)
  }
}

function processPrefetchQueue() {
  if (prefetchRunning || prefetchQueue.length === 0) return
  prefetchRunning = true

  const next = prefetchQueue.shift()!
  const exec = () => {
    next().catch((_e: unknown) => {
      // suppress debug logs in production build
    }).finally(() => {
      prefetchRunning = false
      // Schedule next item after a brief idle to avoid contention
      runWhenIdle(() => processPrefetchQueue())
    })
  }

  if (!shouldPrefetch()) {
    // Skip execution if network conditions are poor; retry after 30s
    prefetchRunning = false
    setTimeout(() => runWhenIdle(processPrefetchQueue), 30_000)
    return
  }
  runWhenIdle(exec)
}

export function schedulePrefetch(task: PrefetchTask) {
  // Cap queue size at 50 items to prevent unbounded growth
  if (prefetchQueue.length >= 50) return
  prefetchQueue.push(task)
  processPrefetchQueue()
}

// Default prefetch strategy for Shell: prioritize Auth MFE when unauthenticated
export async function initMfePrefetch() {
  try {
    const auth = await AuthStateManager.load()
    const isAuthenticated = !!(auth && AuthStateManager.isAuthValid(auth))
    if (!isAuthenticated) {
      schedulePrefetch(async () => {
        await import('@innolab-trader/mfe-auth')
      })
    }
  } catch {
    // If state unknown, be conservative and prefetch auth
    schedulePrefetch(async () => {
      await import('@innolab-trader/mfe-auth')
    })
  }
}
